import React, { useEffect, useState, useCallback } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import {
  Building2, User as UserIcon, Phone, Mail, MessageCircle, Globe,
  Image as ImageIcon, ExternalLink, Link as LinkIcon, FileText,
  Pencil, StickyNote, Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDateTime } from '../../lib/formatUtils';
import { useAuth } from '../../context/AuthContext';

const BASE = process.env.REACT_APP_BACKEND_URL;

/**
 * Renders the customer's Activation Form submission (business details +
 * preferred domains + brand assets) inline on the SMB Lead Detail page.
 *
 * Hidden when the lead hasn't reached `details_submitted` yet — there's
 * no submission row in MongoDB until the customer hits Submit.
 */
const SUBMITTED_STATUSES = [
  'details_submitted', 'in_development', 'awaiting_approval',
  'approved', 'paid', 'website_live', 'completed',
];

const EMPTY_EDIT = {
  business_name: '', owner_name: '', mobile: '', email: '',
  whatsapp: '', phone: '',
  domain_pref_1: '', domain_pref_2: '', domain_pref_3: '',
  social_links: '', notes: '',
};

export const ActivationSubmissionCard = ({ leadId, leadStatus, onChanged }) => {
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('smb.edit');
  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(EMPTY_EDIT);
  const [saving, setSaving] = useState(false);

  const shouldShow = SUBMITTED_STATUSES.includes(leadStatus);

  const fetchSubmission = useCallback(async () => {
    if (!shouldShow) { setSubmission(null); return; }
    setLoading(true);
    try {
      const r = await api.get(`/smb/leads/${leadId}/submission`);
      setSubmission(r.data?.data || null);
    } catch {
      setSubmission(null);
    } finally {
      setLoading(false);
    }
  }, [leadId, shouldShow]);

  useEffect(() => { fetchSubmission(); }, [fetchSubmission]);

  const openEdit = () => {
    if (!submission) return;
    setEditForm({
      business_name: submission.business_name || '',
      owner_name: submission.owner_name || '',
      mobile: submission.mobile || '',
      email: submission.email || '',
      whatsapp: submission.whatsapp || '',
      phone: submission.phone || '',
      domain_pref_1: submission.domain_pref_1 || '',
      domain_pref_2: submission.domain_pref_2 || '',
      domain_pref_3: submission.domain_pref_3 || '',
      social_links: submission.social_links || '',
      notes: submission.notes || '',
    });
    setEditOpen(true);
  };

  const handleSave = async (e) => {
    e?.preventDefault?.();
    setSaving(true);
    try {
      const r = await api.put(`/smb/leads/${leadId}/submission`, editForm);
      if (r.data?.success) {
        setSubmission(r.data.data);
        const changed = r.data.changed_fields || [];
        if (r.data.no_changes || changed.length === 0) {
          toast.info('No changes to save.');
        } else {
          toast.success(`Updated ${changed.length} field${changed.length === 1 ? '' : 's'}.`);
          onChanged?.();
        }
        setEditOpen(false);
      } else {
        toast.error('Failed to save changes.');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save changes.');
    } finally {
      setSaving(false);
    }
  };

  if (!shouldShow) return null;
  if (loading) {
    return (
      <Card data-testid="submission-card-loading">
        <CardHeader className="pb-3"><CardTitle className="text-base">Submitted Details</CardTitle></CardHeader>
        <CardContent><p className="text-sm text-muted-foreground">Loading submission…</p></CardContent>
      </Card>
    );
  }
  if (!submission) {
    return (
      <Card data-testid="submission-card-empty">
        <CardHeader className="pb-3"><CardTitle className="text-base">Submitted Details</CardTitle></CardHeader>
        <CardContent><p className="text-sm text-muted-foreground">No submission found yet.</p></CardContent>
      </Card>
    );
  }

  const fullUrl = (path) => path ? `${BASE}${path}` : null;
  const domains = [submission.domain_pref_1, submission.domain_pref_2, submission.domain_pref_3].filter(Boolean);
  const social = (submission.social_links || '')
    .split(/\r?\n/).map(s => s.trim()).filter(Boolean);

  return (
    <>
      <Card data-testid="submission-card">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <CardTitle className="text-base flex items-center gap-2">
              <FileText className="h-4 w-4 text-emerald-500" />
              Submitted Details
              {submission.smb_project_id && (
                <span
                  className="text-[10px] font-mono px-1.5 py-0.5 rounded border border-emerald-500/40 bg-emerald-500/10 text-emerald-400"
                  title={`Project ID: ${submission.smb_project_id}`}
                  data-testid="submission-pid"
                >
                  PID {String(submission.smb_project_id).slice(-8).toUpperCase()}
                </span>
              )}
            </CardTitle>
            <div className="flex items-center gap-2">
              <Badge className="bg-emerald-500/15 text-emerald-400 border-emerald-500/30 border text-[10px]">
                {submission.submitted_at ? formatDateTime(submission.submitted_at) : ''}
              </Badge>
              {canEdit && (
                <Button
                  size="sm" variant="outline" className="h-7 px-2 text-xs"
                  onClick={openEdit} data-testid="edit-submission-btn"
                >
                  <Pencil className="h-3 w-3 mr-1" />Edit
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4 text-sm">
          {/* Business + contact */}
          <div className="grid sm:grid-cols-2 gap-3" data-testid="submission-business">
            <Field icon={Building2} label="Business name" value={submission.business_name} />
            <Field icon={UserIcon} label="Owner" value={submission.owner_name} />
            <Field icon={Phone} label="Mobile" value={submission.mobile} href={submission.mobile ? `tel:${submission.mobile}` : null} />
            <Field icon={Mail} label="Email" value={submission.email} href={submission.email ? `mailto:${submission.email}` : null} />
            {submission.whatsapp && (
              <Field icon={MessageCircle} label="WhatsApp" value={submission.whatsapp}
                href={`https://wa.me/${(submission.whatsapp || '').replace(/[^0-9]/g, '')}`} extColor="text-green-400" />
            )}
            {submission.phone && <Field icon={Phone} label="Landline" value={submission.phone} />}
          </div>

          {/* Preferred domains */}
          {domains.length > 0 && (
            <div data-testid="submission-domains">
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1.5 flex items-center gap-1.5">
                <Globe className="h-3 w-3" /> Preferred Domains
              </p>
              <ol className="space-y-1">
                {domains.map((d, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <span className="text-[10px] font-mono text-muted-foreground w-5">#{i + 1}</span>
                    <a
                      href={d.startsWith('http') ? d : `https://${d}`}
                      target="_blank" rel="noreferrer"
                      className="text-primary hover:underline break-all"
                    >{d}</a>
                  </li>
                ))}
              </ol>
            </div>
          )}

          {/* Social */}
          {social.length > 0 && (
            <div data-testid="submission-social">
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1.5 flex items-center gap-1.5">
                <LinkIcon className="h-3 w-3" /> Social Links
              </p>
              <ul className="space-y-1">
                {social.map((s, i) => (
                  <li key={i} className="flex items-center gap-1.5">
                    <ExternalLink className="h-3 w-3 text-muted-foreground shrink-0" />
                    <a href={s} target="_blank" rel="noreferrer" className="text-primary hover:underline break-all">{s}</a>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Logo */}
          {submission.logo_url && (
            <div data-testid="submission-logo">
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1.5 flex items-center gap-1.5">
                <ImageIcon className="h-3 w-3" /> Logo
              </p>
              <a href={fullUrl(submission.logo_url)} target="_blank" rel="noreferrer" className="inline-block">
                <img
                  src={fullUrl(submission.logo_url)}
                  alt="Logo"
                  className="h-20 w-auto max-w-[200px] rounded-md border border-border bg-muted/30 object-contain"
                />
              </a>
            </div>
          )}

          {/* Photos */}
          {submission.photo_urls && submission.photo_urls.length > 0 && (
            <div data-testid="submission-photos">
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1.5 flex items-center gap-1.5">
                <ImageIcon className="h-3 w-3" /> Photos ({submission.photo_urls.length})
              </p>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {submission.photo_urls.map((p, i) => (
                  <a key={i} href={fullUrl(p)} target="_blank" rel="noreferrer" className="block">
                    <img
                      src={fullUrl(p)}
                      alt={`Photo ${i + 1}`}
                      className="aspect-square w-full rounded-md border border-border bg-muted/30 object-cover hover:opacity-80 transition-opacity"
                    />
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Notes / Remarks — internal-only, editable */}
          {submission.notes && (
            <div data-testid="submission-notes" className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3">
              <p className="text-[10px] uppercase tracking-wide text-amber-500 mb-1 flex items-center gap-1.5">
                <StickyNote className="h-3 w-3" /> Notes / Remarks
              </p>
              <p className="text-sm whitespace-pre-wrap">{submission.notes}</p>
              {submission.updated_at && (
                <p className="text-[10px] text-muted-foreground mt-1.5">
                  Last edited {formatDateTime(submission.updated_at)}
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit modal */}
      <Dialog open={editOpen} onOpenChange={(o) => { if (!saving) setEditOpen(o); }}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="edit-submission-modal">
          <DialogHeader>
            <DialogTitle>Edit Submitted Details</DialogTitle>
            <DialogDescription>
              Update the customer's activation form details. All changes are logged in the audit trail.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4 py-2">
            <div className="grid sm:grid-cols-2 gap-3">
              <FormField label="Business name" testId="edit-business-name"
                value={editForm.business_name}
                onChange={(v) => setEditForm({ ...editForm, business_name: v })} />
              <FormField label="Owner name" testId="edit-owner-name"
                value={editForm.owner_name}
                onChange={(v) => setEditForm({ ...editForm, owner_name: v })} />
              <FormField label="Mobile" testId="edit-mobile"
                value={editForm.mobile}
                onChange={(v) => setEditForm({ ...editForm, mobile: v })} />
              <FormField label="Email" testId="edit-email"
                value={editForm.email} type="email"
                onChange={(v) => setEditForm({ ...editForm, email: v })} />
              <FormField label="WhatsApp" testId="edit-whatsapp"
                value={editForm.whatsapp}
                onChange={(v) => setEditForm({ ...editForm, whatsapp: v })} />
              <FormField label="Landline / Phone" testId="edit-phone"
                value={editForm.phone}
                onChange={(v) => setEditForm({ ...editForm, phone: v })} />
            </div>
            <div className="space-y-2">
              <Label className="text-xs">Preferred Domains</Label>
              <Input value={editForm.domain_pref_1} data-testid="edit-domain-1"
                placeholder="Preference #1"
                onChange={(e) => setEditForm({ ...editForm, domain_pref_1: e.target.value })} />
              <Input value={editForm.domain_pref_2} data-testid="edit-domain-2"
                placeholder="Preference #2"
                onChange={(e) => setEditForm({ ...editForm, domain_pref_2: e.target.value })} />
              <Input value={editForm.domain_pref_3} data-testid="edit-domain-3"
                placeholder="Preference #3"
                onChange={(e) => setEditForm({ ...editForm, domain_pref_3: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Social links (one per line)</Label>
              <Textarea rows={3} value={editForm.social_links} data-testid="edit-social"
                onChange={(e) => setEditForm({ ...editForm, social_links: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs flex items-center gap-1.5">
                <StickyNote className="h-3 w-3 text-amber-500" />
                Notes / Remarks (internal)
              </Label>
              <Textarea rows={4} value={editForm.notes} data-testid="edit-notes"
                placeholder="Any additional context, client requests, or follow-up info…"
                onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditOpen(false)} disabled={saving}
                data-testid="edit-submission-cancel">
                Cancel
              </Button>
              <Button type="submit" disabled={saving} data-testid="edit-submission-save">
                {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving…</> : 'Save Changes'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

const Field = ({ icon: Icon, label, value, href, extColor }) => {
  if (!value) return null;
  return (
    <div className="space-y-0.5">
      <p className="text-[10px] uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
        <Icon className="h-3 w-3" />{label}
      </p>
      {href ? (
        <a href={href} target={href.startsWith('http') ? '_blank' : undefined} rel="noreferrer"
           className={`${extColor || 'text-primary'} hover:underline break-all`}>{value}</a>
      ) : (
        <p className="break-all">{value}</p>
      )}
    </div>
  );
};

const FormField = ({ label, value, onChange, type = 'text', testId }) => (
  <div className="space-y-1.5">
    <Label className="text-xs">{label}</Label>
    <Input type={type} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId} />
  </div>
);
